import { motion } from "framer-motion";

/**
 * Six clean SVG-and-CSS diagrams for the /marketing page.
 * All diagrams: dark theme, gold accents, fade-in on scroll, responsive.
 */

const fadeIn = {
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
};

const GOLD = "#C9A050";
const MUTED = "#8A8A93";
const TEXT = "#E4E4E7";

/* ---------- 1. INSIGHT split ---------- */
export function InsightDiagram() {
  return (
    <motion.div {...fadeIn} data-testid="diagram-insight" className="my-10 md:my-14">
      <div className="max-w-2xl mx-auto">
        <div className="flex flex-col items-center">
          <Node label="INSIGHT" gold large />
          <Connector vertical />
          <div className="flex w-full justify-between gap-4 md:gap-10">
            <div className="flex-1 flex flex-col items-center">
              <DiagonalLine direction="left" />
              <Node label="PAIN" />
              <p className="mt-3 text-xs md:text-sm text-[#8A8A93] text-center max-w-[14ch]">what they struggle with</p>
            </div>
            <div className="flex-1 flex flex-col items-center">
              <DiagonalLine direction="right" />
              <Node label="DESIRE" />
              <p className="mt-3 text-xs md:text-sm text-[#8A8A93] text-center max-w-[14ch]">what they want</p>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

/* ---------- 2. VALUE — value diverges into pain & desire, both converging on audience ----------
   Layout:
              PAIN  →
   VALUE →                  → AUDIENCE
              DESIRE →
*/
export function ValueDiagram() {
  return (
    <motion.div {...fadeIn} data-testid="diagram-value" className="my-10 md:my-14">
      <div className="max-w-4xl mx-auto">
        {/* Desktop: VALUE → fork into PAIN/DESIRE → merge into AUDIENCE */}
        <div className="hidden md:grid grid-cols-[auto_1fr_auto_1fr_auto] gap-3 md:gap-6 items-center">
          <Node label="VALUE" gold />
          <DivergeLines />
          <div className="flex flex-col gap-3 md:gap-4">
            <Node label="PAIN" small />
            <Node label="DESIRE" small />
          </div>
          <ConvergeLines />
          <Node label="AUDIENCE" />
        </div>

        {/* Mobile: vertical fork — VALUE on top, splits to PAIN+DESIRE side-by-side, both merging into AUDIENCE */}
        <div className="md:hidden flex flex-col items-center gap-4">
          <Node label="VALUE" gold />
          <Connector vertical />
          <div className="flex gap-4">
            <Node label="PAIN" small />
            <Node label="DESIRE" small />
          </div>
          <Connector vertical />
          <Node label="AUDIENCE" />
        </div>
      </div>
    </motion.div>
  );
}

/* Two diverging diagonal lines from a single point on the left → two points on the right (VALUE → PAIN/DESIRE). */
function DivergeLines() {
  return (
    <svg viewBox="0 0 100 60" className="w-full h-12 md:h-14" preserveAspectRatio="none" aria-hidden="true">
      <line x1="0" y1="30" x2="100" y2="14" stroke={GOLD} strokeOpacity="0.5" strokeWidth="0.6" vectorEffect="non-scaling-stroke" />
      <line x1="0" y1="30" x2="100" y2="46" stroke={GOLD} strokeOpacity="0.5" strokeWidth="0.6" vectorEffect="non-scaling-stroke" />
      <polyline points="94,10 100,14 94,18" fill="none" stroke={GOLD} strokeOpacity="0.6" strokeWidth="0.6" vectorEffect="non-scaling-stroke" />
      <polyline points="94,42 100,46 94,50" fill="none" stroke={GOLD} strokeOpacity="0.6" strokeWidth="0.6" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

/* Two converging diagonal lines (PAIN + DESIRE → single point on right = AUDIENCE) */
function ConvergeLines() {
  return (
    <svg viewBox="0 0 100 60" className="w-full h-12 md:h-14" preserveAspectRatio="none" aria-hidden="true">
      <line x1="0" y1="14" x2="100" y2="30" stroke={GOLD} strokeOpacity="0.5" strokeWidth="0.6" vectorEffect="non-scaling-stroke" />
      <line x1="0" y1="46" x2="100" y2="30" stroke={GOLD} strokeOpacity="0.5" strokeWidth="0.6" vectorEffect="non-scaling-stroke" />
      <polyline points="94,26 100,30 94,34" fill="none" stroke={GOLD} strokeOpacity="0.6" strokeWidth="0.6" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

/* ---------- 3. CONTENT flow ---------- */
export function ContentDiagram() {
  const steps = ["INSIGHT", "CONTENT", "FEELING", "ACTION"];
  return (
    <motion.div {...fadeIn} data-testid="diagram-content" className="my-10 md:my-14">
      <div className="max-w-4xl mx-auto">
        <div className="flex flex-wrap md:flex-nowrap items-center justify-center gap-3 md:gap-4">
          {steps.map((s, i) => (
            <div key={s} className="flex items-center gap-3 md:gap-4">
              <Node label={s} gold={i === 1} />
              {i < steps.length - 1 && <Arrow />}
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  );
}

/* ---------- 4. DISTRIBUTION compare ---------- */
export function DistributionDiagram({ onPlatformClick }) {
  return (
    <motion.div {...fadeIn} data-testid="diagram-distribution" className="my-10 md:my-14">
      <div className="max-w-3xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-8">
        <PlatformCard
          icon={<IconInstagram />}
          name="Instagram"
          tag="Short, discovery"
          desc="Rewards content that holds attention quickly. Reels, hooks, share-worthy frames."
          onClick={() => onPlatformClick && onPlatformClick("instagram")}
          testid="distribution-card-instagram"
        />
        <PlatformCard
          icon={<IconYoutube />}
          name="YouTube"
          tag="Long, trust"
          desc="Rewards watch time and retention. Builds depth, authority, and lasting audience."
          onClick={() => onPlatformClick && onPlatformClick("youtube")}
          testid="distribution-card-youtube"
        />
      </div>
    </motion.div>
  );
}

function PlatformCard({ icon, name, tag, desc, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="text-left mk-card-gold p-6 md:p-8 hover:scale-[1.012] transition-transform duration-500 cursor-pointer"
    >
      <div className="flex items-center gap-3 mb-4">
        <div className="text-[#C9A050]">{icon}</div>
        <h4 className="font-display text-2xl md:text-3xl">{name}</h4>
      </div>
      <p className="overline text-[#C9A050]/80 mb-3">{tag}</p>
      <p className="text-[#A1A1AA] text-sm md:text-base leading-relaxed">{desc}</p>
      <p className="overline text-[#C9A050]/60 mt-5">Read more →</p>
    </button>
  );
}

/* ---------- 5. FUNNEL ---------- */
export function FunnelDiagram() {
  const steps = [
    { label: "SEE", w: 100 },
    { label: "UNDERSTAND", w: 84 },
    { label: "TRUST", w: 68 },
    { label: "DECIDE", w: 52 },
    { label: "BUY", w: 36, gold: true },
  ];
  return (
    <motion.div {...fadeIn} data-testid="diagram-funnel" className="my-10 md:my-14">
      <div className="max-w-md mx-auto flex flex-col items-center gap-2.5">
        {steps.map((s) => (
          <div
            key={s.label}
            className="relative h-12 md:h-14 flex items-center justify-center transition-all"
            style={{ width: `${s.w}%` }}
          >
            <div
              className={`absolute inset-0 ${s.gold ? "bg-[#C9A050]/15 border-[#C9A050]/60" : "bg-white/[0.03] border-white/15"} border`}
              style={{ clipPath: "polygon(0 0, 100% 0, 95% 100%, 5% 100%)" }}
            />
            <span
              className={`relative overline ${s.gold ? "text-[#C9A050]" : "text-[#E4E4E7]"}`}
              style={{ letterSpacing: "0.32em" }}
            >
              {s.label}
            </span>
          </div>
        ))}
      </div>
    </motion.div>
  );
}

/* ---------- 6. FULL SYSTEM CHAIN — clickable jumps ---------- */
export function SystemDiagram({ onNodeClick }) {
  // Each node maps to a section anchor id on /marketing
  const nodes = [
    { label: "AUDIENCE", anchor: "mk-anchor-audience", key: "audience" },
    { label: "INSIGHT", anchor: "mk-anchor-insight", key: "insight" },
    { label: "VALUE", anchor: "mk-anchor-value", key: "value" },
    { label: "POSITIONING", anchor: "mk-anchor-positioning", key: "positioning" },
    { label: "BRAND STRATEGY", anchor: "mk-anchor-brand-strategy", key: "brand-strategy" },
    { label: "CONTENT", anchor: "mk-anchor-content", key: "content" },
    { label: "DISTRIBUTION", anchor: "mk-anchor-distribution", key: "distribution" },
    { label: "FUNNEL", anchor: "mk-anchor-funnel", key: "funnel" },
    { label: "CONVERSION", anchor: "mk-anchor-funnel", key: "conversion" },
  ];
  return (
    <motion.div {...fadeIn} data-testid="diagram-system" className="my-12 md:my-16">
      <div className="max-w-5xl mx-auto">
        <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-3 md:gap-x-3 md:gap-y-4">
          {nodes.map((n, i) => (
            <div key={n.key} className="flex items-center gap-2 md:gap-3">
              <button
                type="button"
                data-testid={`system-node-${n.key}`}
                onClick={() => onNodeClick && onNodeClick(n.anchor)}
                className={`overline px-3 py-2 md:px-4 md:py-2.5 border transition-all duration-500 cursor-pointer ${
                  i === nodes.length - 1
                    ? "border-[#C9A050] text-[#C9A050] bg-[#C9A050]/5 hover:bg-[#C9A050]/10"
                    : "border-white/15 text-[#E4E4E7] hover:border-[#C9A050]/60 hover:text-[#C9A050]"
                }`}
              >
                {n.label}
              </button>
              {i < nodes.length - 1 && (
                <span className="text-[#8A8A93] text-xs">→</span>
              )}
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  );
}

/* ---------- primitives ---------- */
function Node({ label, gold, large, small }) {
  const base =
    "inline-flex items-center justify-center border transition-all duration-500";
  const size = large
    ? "px-6 py-3 md:px-8 md:py-4 text-sm md:text-base"
    : small
    ? "px-3 py-1.5 md:px-4 md:py-2 text-[10px] md:text-xs"
    : "px-4 py-2 md:px-6 md:py-2.5 text-xs md:text-sm";
  const tone = gold
    ? "border-[#C9A050] text-[#C9A050] bg-[#C9A050]/5"
    : "border-white/20 text-[#E4E4E7]";
  return (
    <span
      className={`${base} ${size} ${tone}`}
      style={{ letterSpacing: "0.32em", fontWeight: 500, fontFamily: "Manrope, sans-serif", textTransform: "uppercase" }}
    >
      {label}
    </span>
  );
}

function Connector({ vertical }) {
  return (
    <span
      className={`block ${vertical ? "h-10 w-px" : "w-10 h-px"} bg-[#C9A050]/40`}
      aria-hidden="true"
    />
  );
}

function DiagonalLine({ direction = "left" }) {
  return (
    <svg
      viewBox="0 0 100 40"
      className="w-full h-8 md:h-10 mb-2"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <line
        x1={direction === "left" ? "100" : "0"}
        y1="0"
        x2={direction === "left" ? "0" : "100"}
        y2="40"
        stroke={GOLD}
        strokeOpacity="0.4"
        strokeWidth="0.6"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

function Arrow() {
  return (
    <svg
      viewBox="0 0 40 12"
      className="w-8 md:w-10 h-3 text-[#C9A050]/60"
      aria-hidden="true"
    >
      <line
        x1="0"
        y1="6"
        x2="34"
        y2="6"
        stroke="currentColor"
        strokeWidth="1"
        vectorEffect="non-scaling-stroke"
      />
      <polyline
        points="30,2 36,6 30,10"
        fill="none"
        stroke="currentColor"
        strokeWidth="1"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

function IconInstagram() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="3" y="3" width="18" height="18" rx="5" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="17.5" cy="6.5" r="0.6" fill="currentColor" />
    </svg>
  );
}
function IconYoutube() {
  return (
    <svg width="24" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="2" y="5" width="20" height="14" rx="3" />
      <polygon points="10,9 16,12 10,15" fill="currentColor" />
    </svg>
  );
}
